import { useReactFlow } from "@xyflow/react";
import { Play, Sparkles, Maximize } from "lucide-react";
import { useCanvasStore } from "@/store/useCanvasStore";
import { useNodePageStore, type PanelTreatment } from "@/store/useNodePageStore";
import { CV } from "@/config/canvasSizing";

// Bottom playback bar (per design mock): enter the walk-through, rebuild the map
// (re-run Dagre auto-layout), fit the view, and switch the pages-panel treatment.
// Rendered as a ReactFlow <Panel> so it can drive the camera via useReactFlow.

const TREATMENTS: { key: PanelTreatment; label: string }[] = [
  { key: "drawer", label: "Drawer" },
  { key: "panel",  label: "Panel" },
  { key: "glass",  label: "Glass" },
];

const FIT = { padding: 0.2, duration: 500, maxZoom: CV.zoom.max } as const;

const BTN_BASE = "flex items-center gap-1.5 px-3 py-2 text-[12.5px] font-semibold rounded-[9px] transition-colors whitespace-nowrap";
const BTN_PRIMARY = `${BTN_BASE} bg-gradient-to-br from-indigo-500 to-indigo-400 text-white shadow-[0_6px_18px_-6px_rgba(99,102,241,0.7)] hover:from-indigo-400 hover:to-indigo-300`;
const BTN_GHOST = `${BTN_BASE} text-zinc-400 hover:text-zinc-100 hover:bg-white/5`;

export default function CanvasPlaybar() {
  const rf = useReactFlow();
  const enterRevealMode = useCanvasStore((s) => s.enterRevealMode);
  const rebuildLayout   = useCanvasStore((s) => s.rebuildLayout);
  const treatment       = useNodePageStore((s) => s.treatment);
  const setTreatment    = useNodePageStore((s) => s.setTreatment);

  function handleRebuild() {
    rebuildLayout();
    // Re-frame once the new positions are in the RF store.
    requestAnimationFrame(() => void rf.fitView(FIT));
  }

  return (
    <div
      data-testid="canvas-playbar"
      className="flex items-center gap-1 p-1.5 rounded-2xl bg-zinc-900 border border-white/[0.09] shadow-[0_16px_40px_-12px_rgba(0,0,0,0.7)]"
    >
      <button data-testid="playbar-walkthrough" onClick={enterRevealMode} className={BTN_PRIMARY}>
        <Play size={15} />
        Walk-through
      </button>
      <button data-testid="playbar-rebuild" onClick={handleRebuild} className={BTN_GHOST}>
        <Sparkles size={15} />
        Rebuild map
      </button>
      <button data-testid="playbar-fit" onClick={() => void rf.fitView(FIT)} className={BTN_GHOST}>
        <Maximize size={15} />
        Fit
      </button>
      <div className="w-px h-5 bg-zinc-600 mx-1" />
      <div className="flex items-center gap-0.5 bg-[#0f0f12] border border-zinc-800 rounded-[9px] p-0.5">
        {TREATMENTS.map((t) => (
          <button
            key={t.key}
            data-testid={`treatment-${t.key}`}
            onClick={() => setTreatment(t.key)}
            className={`px-2.5 py-1.5 text-[11.5px] font-semibold rounded-md transition-colors ${
              treatment === t.key ? "bg-indigo-500 text-white" : "text-zinc-400 hover:text-zinc-200"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>
    </div>
  );
}
